import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaginatedQuery, useQuery } from 'convex/react';
import { api } from '../../api';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useInstalledApps } from '@/hooks/useInstalledApps';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, CheckCircle2, RefreshCw } from 'lucide-react';
import { showSKOverlay } from '@/utils/nativeInterface';
import { ProfileAvatar } from '@/components/ProfileAvatar';

const Updates: React.FC = () => {
  const navigate = useNavigate();
  const { serial } = useEffectiveSerial();
  
  // Inline queries for available apps
  const { results: filterResults, status } = usePaginatedQuery(
    (api.apps.list as any).allowedByDeviceFilter,
    serial ? { serial } : "skip",
    { initialNumItems: 50 }
  );

  const staticResults = useQuery(
    (api.apps.list as any).allowedByDeviceStatic,
    serial ? { serial } : "skip"
  );

  // Merge all available apps for lookup
  const allApps = useMemo(() => {
    const all = [...(filterResults ?? []), ...(staticResults ?? [])];
    return all.map((app: any, i: number) => ({
      id: app.id ? String(app.id) : `temp-${app.trackId || i}`,
      name: app.name || 'Unknown App',
      icon: app.iconUrl || '/placeholder.svg',
      description: '',
      version: '',
      appStoreId: String(app.trackId || ''),
      category: app.category || undefined,
    }));
  }, [filterResults, staticResults]);

  const isLoadingAvailableApps = filterResults === undefined || staticResults === undefined;

  const { installedApps, isLoading: isLoadingInstalledApps } = useInstalledApps();

  // Backend returns apps that need updates - just look up their details
  const appsNeedingUpdates = useMemo(() => {
    if (!installedApps || installedApps.length === 0) return [];
    
    return installedApps
      .map(updateApp => {
        const appDetails = allApps.find(
          app => app.appStoreId === updateApp.trackId
        );
        
        if (!appDetails) return null;
        
        return {
          ...appDetails,
          currentVersion: updateApp.version || 'Unknown'
        };
      })
      .filter(app => app !== null);
  }, [installedApps, allApps]);

  const handleUpdate = async (appStoreId: string) => {
    try {
      await showSKOverlay(appStoreId);
    } catch (error) {
      console.error('Error showing SKOverlay:', error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const isLoading = isLoadingAvailableApps || isLoadingInstalledApps;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-24">
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur-lg border-b border-border/50">
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center hover:bg-muted active:scale-[0.97] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 text-foreground" aria-hidden="true" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                  <RefreshCw className="w-5 h-5 text-primary-foreground" />
                </div>
                <h1 className="text-2xl font-bold tracking-tight text-foreground">Updates</h1>
              </div>
              {appsNeedingUpdates.length > 0 && (
                <div className="bg-destructive text-destructive-foreground text-xs px-2.5 py-1 rounded-full font-semibold">
                  {appsNeedingUpdates.length}
                </div>
              )}
            </div>
            <ProfileAvatar />
          </div>
          <p className="text-[13px] text-muted-foreground mt-3 ml-[52px]">Update your installed apps</p>
        </div>
      </header>

      <div className="px-4 pt-4">
        <div className="mb-4 p-3 bg-muted/50 rounded-xl border border-border/50">
          <p className="text-[13px] text-muted-foreground">
            <span className="font-medium text-foreground">Note:</span> If the App Store shows "Open" instead of "Update", the app is already up to date on your device.
          </p>
        </div>
      </div>

      <div className="px-4">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-background rounded-2xl border border-border/50 p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-muted rounded-xl"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                  <div className="w-20 h-9 bg-muted rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        ) : appsNeedingUpdates.length > 0 ? (
          <div className="space-y-3">
            {appsNeedingUpdates.map((app) => (
              <div key={app.id} className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                <div className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-muted rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
                    <img 
                      src={app.icon} 
                      alt={app.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[15px] text-foreground truncate">{app.name}</h3>
                    <p className="text-[13px] text-muted-foreground mt-0.5">v{app.currentVersion}</p>
                  </div>
                  
                  <Button
                    size="sm"
                    onClick={() => handleUpdate(app.appStoreId)}
                    className="flex-shrink-0 gap-1.5"
                    disabled={!app.appStoreId || app.appStoreId.trim() === ''}
                  >
                    <Download className="w-4 h-4" />
                    Update
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-500/10 rounded-2xl flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">All apps are up to date</h2>
            <p className="text-[14px] text-muted-foreground">No apps need to be updated at this time.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Updates;
